import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { pillarArticles } from '@/mocks/pillarArticles';

export default function ArticlePage({ slug }: { slug: string }) {
  const article = pillarArticles.find((a) => a.slug === slug);
  const [tocOpen, setTocOpen] = useState(true);

  useEffect(() => {
    document.title = `${article ? article.title : 'Article'} | CSSBuy Spreadsheet`;
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="max-w-3xl mx-auto px-4 md:px-6 pt-32 pb-24 text-center">
          <h1 className="font-heading text-3xl text-foreground-950 mb-4">Article not found</h1>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors">
            Back to home
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = pillarArticles.filter((a) => a.slug !== article.slug).slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Breadcrumb */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line" style={{ width: 16, height: 16 }} />
            <Link to="/cssbuy-spreadsheet" className="hover:text-primary-600 transition-colors">CSSBuy Spreadsheet</Link>
            <i className="ri-arrow-right-s-line" style={{ width: 16, height: 16 }} />
            <span className="text-foreground-700">{article.title}</span>
          </nav>
        </div>

        <article className="py-8 md:py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Article Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-4 flex-wrap">
                <span>Published: 27 Aug 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span>Updated: 27 Aug 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span>{article.readTime} min read</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6">
                {article.title}
              </h1>
              <p className="text-lg text-foreground-700 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            {/* Hero Image */}
            <div className="rounded-2xl overflow-hidden mb-10 shadow-lg">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Table of Contents */}
            <div className="bg-background-100 rounded-xl p-5 mb-10">
              <button onClick={() => setTocOpen(!tocOpen)} className="w-full flex items-center justify-between">
                <h2 className="font-semibold text-foreground-950">Contents</h2>
                <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${tocOpen ? 'rotate-180' : ''}`} style={{ width: 20, height: 20 }} />
              </button>
              {tocOpen && (
                <ul className="mt-3 space-y-2">
                  {article.sections.map((s, i) => (
                    <li key={i}>
                      <a href={`#section-${i}`} className="text-sm text-foreground-700 hover:text-primary-600 transition-colors">
                        {s.heading}
                      </a>
                    </li>
                  ))}
                  <li>
                    <a href="#comparison" className="text-sm text-foreground-700 hover:text-primary-600 transition-colors">Comparison Table</a>
                  </li>
                  <li>
                    <a href="#faq" className="text-sm text-foreground-700 hover:text-primary-600 transition-colors">FAQ</a>
                  </li>
                </ul>
              )}
            </div>

            {/* TL;DR */}
            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-6 md:p-8 mb-10">
              <h2 className="font-heading text-xl text-foreground-950 mb-3">TL;DR</h2>
              <p className="text-sm text-foreground-700 leading-relaxed">{article.tldr}</p>
            </div>

            {/* Sections */}
            {article.sections.map((s, i) => (
              <div key={i} id={`section-${i}`} className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{s.heading}</h2>
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="text-foreground-700 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
              </div>
            ))}

            {/* Comparison Table */}
            <div id="comparison" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">Quick Comparison</h2>
              <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Feature</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">CSSBuy</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Superbuy</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Sugargoo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {article.table.map((row, i) => (
                      <tr key={i} className={`border-t border-background-200/70 ${i % 2 === 1 ? 'bg-background-50' : ''}`}>
                        <td className="px-4 py-3 text-foreground-700">{row.feature}</td>
                        <td className="px-4 py-3">{row.cssbuy === '✓' ? <i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /> : <span className="text-foreground-600">{row.cssbuy}</span>}</td>
                        <td className="px-4 py-3">{row.superbuy === '✓' ? <i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /> : <span className="text-foreground-600">{row.superbuy}</span>}</td>
                        <td className="px-4 py-3">{row.sugargoo === '✓' ? <i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /> : <span className="text-foreground-600">{row.sugargoo}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-foreground-500">Information may change. Verify current details before ordering.</p>
            </div>

            {/* FAQ */}
            <div id="faq" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">Frequently Asked Questions</h2>
              <div className="space-y-3">
                {article.faqs.map((f, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-5">
                    <h3 className="font-semibold text-foreground-950 mb-2">{f.q}</h3>
                    <p className="text-sm text-foreground-700 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Articles */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="font-semibold text-foreground-950 mb-4">Related Articles</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/cssbuy-spreadsheet" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">CSSBuy Spreadsheet</Link>
                <Link to="/cssbuy-review" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">CSSBuy Review</Link>
                <Link to="/cssbuy-shipping" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">Shipping Guide</Link>
                <Link to="/cssbuy-qc" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">QC Guide</Link>
                <Link to="/cssbuy-safety" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">Safety Center</Link>
                <Link to="/" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">Home</Link>
                {related.map((r) => (
                  <Link key={r.slug} to={`/${r.slug}`} className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                    {r.title}
                  </Link>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 text-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/25 whitespace-nowrap"
              >
                Explore CSSBuy Spreadsheet
                <i className="ri-arrow-right-line" style={{ width: 18, height: 18 }} />
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}